import { useState } from 'react';
import { useEditorRef, useEditorSelector } from 'platejs/react';
import { toggleList, someList, ListStyleType } from '@platejs/list';
import { getTableAbove } from '@platejs/table';
import {
    Bold,
    Italic,
    Underline,
    Strikethrough,
    List,
    ListOrdered,
    Quote,
    Table,
    Link,
    Heading1,
    Heading2,
    Heading3,
    Unlink,
    Trash2,
} from 'lucide-react';
import { Button } from '~/components/ui/button';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '~/components/ui/popover';
import { Separator } from '~/components/ui/separator';
import { cn } from '~/lib/utils';

type EditorToolbarProps = {
    disabled?: boolean;
};

type ToolbarButtonProps = {
    onClick: () => void;
    isActive?: boolean;
    disabled?: boolean;
    children: React.ReactNode;
    title?: string;
};

const ToolbarButton = ({
    onClick,
    isActive = false,
    disabled = false,
    children,
    title,
}: ToolbarButtonProps) => (
    <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
            'h-8 w-8 p-0',
            isActive && 'bg-muted'
        )}
        onClick={onClick}
        disabled={disabled}
        title={title}
    >
        {children}
    </Button>
);

const MAX_GRID = 8;

const TableGridPicker = ({ onSelect, disabled }: { onSelect: (rows: number, cols: number) => void; disabled?: boolean }) => {
    const [hoverRows, setHoverRows] = useState(0);
    const [hoverCols, setHoverCols] = useState(0);
    const [open, setOpen] = useState(false);

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={disabled}
                    title="Tabelle einfügen"
                >
                    <Table className="h-4 w-4" />
                </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-2" align="start">
                <div className="text-xs text-muted-foreground mb-1.5 text-center">
                    {hoverRows > 0 ? `${hoverRows} × ${hoverCols}` : 'Tabellengröße wählen'}
                </div>
                <div
                    className="grid gap-0.5"
                    style={{ gridTemplateColumns: `repeat(${MAX_GRID}, 1fr)` }}
                    onMouseLeave={() => { setHoverRows(0); setHoverCols(0); }}
                >
                    {Array.from({ length: MAX_GRID * MAX_GRID }, (_, i) => {
                        const row = Math.floor(i / MAX_GRID) + 1;
                        const col = (i % MAX_GRID) + 1;
                        const isHighlighted = row <= hoverRows && col <= hoverCols;
                        return (
                            <div
                                key={i}
                                className={cn(
                                    'w-4 h-4 border rounded-[2px] cursor-pointer transition-colors',
                                    isHighlighted
                                        ? 'bg-primary border-primary'
                                        : 'bg-background border-border hover:border-muted-foreground'
                                )}
                                onMouseEnter={() => { setHoverRows(row); setHoverCols(col); }}
                                onClick={() => { onSelect(row, col); setOpen(false); setHoverRows(0); setHoverCols(0); }}
                            />
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
};

const TableRowIcon = ({ highlightColor, className }: { highlightColor: string; className?: string }) => (
    <svg viewBox="0 0 16 16" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="14" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="1" y="6" width="14" height="4" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="1" y="11" width="14" height="4" rx="1" stroke={highlightColor} strokeWidth="1.2" fill={highlightColor} fillOpacity="0.15" />
    </svg>
);

const TableColIcon = ({ highlightColor, className }: { highlightColor: string; className?: string }) => (
    <svg viewBox="0 0 16 16" fill="none" className={className} xmlns="http://www.w3.org/2000/svg">
        <rect x="1" y="1" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="6" y="1" width="4" height="14" rx="1" stroke="currentColor" strokeWidth="1.2" />
        <rect x="11" y="1" width="4" height="14" rx="1" stroke={highlightColor} strokeWidth="1.2" fill={highlightColor} fillOpacity="0.15" />
    </svg>
);

export const EditorToolbar = ({ disabled = false }: EditorToolbarProps) => {
    const editor = useEditorRef();

    const isBold = useEditorSelector(
        (editor) => editor.api.mark.isActive?.('bold') ?? false,
        []
    );
    const isItalic = useEditorSelector(
        (editor) => editor.api.mark.isActive?.('italic') ?? false,
        []
    );
    const isUnderline = useEditorSelector(
        (editor) => editor.api.mark.isActive?.('underline') ?? false,
        []
    );
    const isStrikethrough = useEditorSelector(
        (editor) => editor.api.mark.isActive?.('strikethrough') ?? false,
        []
    );
    const isBulletList = useEditorSelector(
        (editor) => someList(editor, ListStyleType.Disc),
        []
    );
    const isNumberedList = useEditorSelector(
        (editor) => someList(editor, ListStyleType.Decimal),
        []
    );
    const isInTable = useEditorSelector(
        (editor) => !!getTableAbove(editor),
        []
    );

    const toggleBold = () => editor.tf.bold?.toggle?.();
    const toggleItalic = () => editor.tf.italic?.toggle?.();
    const toggleUnderline = () => editor.tf.underline?.toggle?.();
    const toggleStrikethrough = () => editor.tf.strikethrough?.toggle?.();

    const insertHeading = (level: 1 | 2 | 3) => {
        const key = `h${level}` as const;
        editor.tf[key]?.toggle?.();
    };

    const toggleBlockquote = () => {
        editor.tf.blockquote?.toggle?.();
    };

    const toggleBulletList = () => {
        toggleList(editor, { listStyleType: ListStyleType.Disc });
    };

    const toggleNumberedList = () => {
        toggleList(editor, { listStyleType: ListStyleType.Decimal });
    };

    const insertTable = (rows: number, cols: number) => {
        editor.tf.insert?.table?.({ rowCount: rows, colCount: cols });
    };

    const insertLink = () => {
        const url = window.prompt('Link URL eingeben:');
        if (url) {
            editor.tf.a?.insert?.({ url });
        }
    };

    const removeLink = () => {
        editor.tf.a?.unwrap?.();
    };

    return (
        <div className="flex flex-wrap items-center gap-0.5 border-b border-border px-2 py-1 bg-muted rounded-t-md">
            <ToolbarButton onClick={toggleBold} isActive={isBold} disabled={disabled} title="Fett (Ctrl+B)">
                <Bold className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton onClick={toggleItalic} isActive={isItalic} disabled={disabled} title="Kursiv (Ctrl+I)">
                <Italic className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton onClick={toggleUnderline} isActive={isUnderline} disabled={disabled} title="Unterstrichen (Ctrl+U)">
                <Underline className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton onClick={toggleStrikethrough} isActive={isStrikethrough} disabled={disabled} title="Durchgestrichen">
                <Strikethrough className="h-4 w-4" />
            </ToolbarButton>

            <Separator orientation="vertical" className="mx-1 h-6" />

            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button type="button" variant="ghost" size="sm" className="h-8 px-2" disabled={disabled}>
                        <Heading1 className="h-4 w-4 mr-1" />
                        <span className="text-xs">Überschrift</span>
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                    <DropdownMenuItem onClick={() => insertHeading(1)}>
                        <Heading1 className="h-4 w-4 mr-2" /> Überschrift 1
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => insertHeading(2)}>
                        <Heading2 className="h-4 w-4 mr-2" /> Überschrift 2
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => insertHeading(3)}>
                        <Heading3 className="h-4 w-4 mr-2" /> Überschrift 3
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <ToolbarButton onClick={toggleBlockquote} disabled={disabled} title="Zitat">
                <Quote className="h-4 w-4" />
            </ToolbarButton>

            <Separator orientation="vertical" className="mx-1 h-6" />

            <ToolbarButton onClick={toggleBulletList} isActive={isBulletList} disabled={disabled} title="Aufzählung">
                <List className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton onClick={toggleNumberedList} isActive={isNumberedList} disabled={disabled} title="Nummerierte Liste">
                <ListOrdered className="h-4 w-4" />
            </ToolbarButton>

            <Separator orientation="vertical" className="mx-1 h-6" />

            <TableGridPicker onSelect={insertTable} disabled={disabled} />

            {isInTable && (
                <>
                    <ToolbarButton onClick={() => editor.tf.insert?.tableRow?.()} disabled={disabled} title="Zeile einfügen">
                        <TableRowIcon highlightColor="#16a34a" className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.tf.remove?.tableRow?.()} disabled={disabled} title="Zeile entfernen">
                        <TableRowIcon highlightColor="#dc2626" className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.tf.insert?.tableColumn?.()} disabled={disabled} title="Spalte einfügen">
                        <TableColIcon highlightColor="#16a34a" className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.tf.remove?.tableColumn?.()} disabled={disabled} title="Spalte entfernen">
                        <TableColIcon highlightColor="#dc2626" className="h-4 w-4" />
                    </ToolbarButton>
                    <ToolbarButton onClick={() => editor.tf.remove?.table?.()} disabled={disabled} title="Tabelle löschen">
                        <Trash2 className="h-4 w-4 text-destructive" />
                    </ToolbarButton>
                </>
            )}

            <Separator orientation="vertical" className="mx-1 h-6" />

            <ToolbarButton onClick={insertLink} disabled={disabled} title="Link einfügen">
                <Link className="h-4 w-4" />
            </ToolbarButton>
            <ToolbarButton onClick={removeLink} disabled={disabled} title="Link entfernen">
                <Unlink className="h-4 w-4" />
            </ToolbarButton>
        </div>
    );
};

export default EditorToolbar;
